import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import styles from './MenuEditor.module.css';

const MenuEditor = () => {
  const { hotelData, saveMenuData, loading } = useData();
  const [menuItems, setMenuItems] = useState({});
  const [sectionOrder, setSectionOrder] = useState([]);
  const [customSections, setCustomSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingItem, setEditingItem] = useState(null);
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  // Default categories for menu items (for backward compatibility)
  const defaultCategories = [
    { key: 'starters', label: 'Starters' },
    { key: 'pubClassics', label: 'Pub Classics' },
    { key: 'curries', label: 'Curries' },
    { key: 'fromTheSea', label: 'From the Sea' },
    { key: 'burgers', label: 'Burgers' },
    { key: 'desserts', label: 'Desserts' },
    { key: 'sides', label: 'Sides' },
    { key: 'drinks', label: 'Drinks' }
  ];

  useEffect(() => {
    if (hotelData?.menuItems) {
      setMenuItems({ ...hotelData.menuItems });
      
      // Set up section ordering
      const savedOrder = hotelData.menuSectionOrder || [];
      const existingSections = Object.keys(hotelData.menuItems);
      
      // Merge saved order with any new sections
      const allSections = [...new Set([...savedOrder, ...existingSections])];
      setSectionOrder(allSections);
      
      // Set up custom section names
      setCustomSections(hotelData.menuSectionNames || {});
    }
  }, [hotelData]);

  const getSectionLabel = (sectionKey) => {
    // Check if it's a custom section name first
    if (customSections[sectionKey]) {
      return customSections[sectionKey];
    }
    
    // Fall back to default categories
    const defaultCategory = defaultCategories.find(cat => cat.key === sectionKey);
    return defaultCategory ? defaultCategory.label : sectionKey;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    const orderedMenuItems = sectionOrder.reduce((sections, sectionKey) => {
      sections[sectionKey] = menuItems[sectionKey] || [];
      return sections;
    }, {});
    const orderedCustomSections = sectionOrder.reduce((sections, sectionKey) => {
      if (customSections[sectionKey]) {
        sections[sectionKey] = customSections[sectionKey];
      }
      return sections;
    }, {});

    const result = await saveMenuData(orderedMenuItems, sectionOrder, orderedCustomSections);

    if (result.success) {
      setMessage({ type: 'success', text: 'Menu updated successfully!' });
      setEditingItem(null);
    } else {
      setMessage({ type: 'error', text: 'Failed to update menu: ' + result.error });
    }

    setSaving(false);
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleReset = () => {
    if (hotelData?.menuItems) {
      setMenuItems({ ...hotelData.menuItems });
      setSectionOrder(hotelData.menuSectionOrder || Object.keys(hotelData.menuItems));
      setCustomSections(hotelData.menuSectionNames || {});
      setEditingItem(null);
      setAddingSection(false);
      setNewSectionName('');
      setMessage({ type: 'info', text: 'Changes reset to last saved version' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleAddSection = () => {
    if (!newSectionName.trim()) return;
    
    const sectionKey = newSectionName.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (!sectionKey) return;
    
    // Add to menu items (empty array)
    const updatedMenuItems = { ...menuItems };
    updatedMenuItems[sectionKey] = [];
    setMenuItems(updatedMenuItems);
    
    // Add to section order
    const updatedOrder = [...sectionOrder, sectionKey];
    setSectionOrder(updatedOrder);
    
    // Add custom section name
    const updatedCustomSections = { ...customSections };
    updatedCustomSections[sectionKey] = newSectionName.trim();
    setCustomSections(updatedCustomSections);
    
    // Reset add section form
    setAddingSection(false);
    setNewSectionName('');
  };

  const handleDeleteSection = (sectionKey) => {
    if (window.confirm('Are you sure you want to delete this entire section and all its items?')) {
      // Remove from menu items
      const updatedMenuItems = { ...menuItems };
      delete updatedMenuItems[sectionKey];
      setMenuItems(updatedMenuItems);
      
      // Remove from section order
      const updatedOrder = sectionOrder.filter(key => key !== sectionKey);
      setSectionOrder(updatedOrder);
      
      // Remove from custom sections
      const updatedCustomSections = { ...customSections };
      delete updatedCustomSections[sectionKey];
      setCustomSections(updatedCustomSections);
      
      // Clear editing if it was this section
      if (editingItem && editingItem.startsWith(sectionKey + '-')) {
        setEditingItem(null);
      }
    }
  };

  // Section ordering handlers
  const handleMoveSection = (sectionKey, direction) => {
    const currentIndex = sectionOrder.indexOf(sectionKey);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= sectionOrder.length) return;
    
    const newOrder = [...sectionOrder];
    [newOrder[currentIndex], newOrder[newIndex]] = [newOrder[newIndex], newOrder[currentIndex]];
    setSectionOrder(newOrder);
  };

  const handleItemUpdate = (category, index, field, value) => {
    const updated = { ...menuItems };
    if (!updated[category]) updated[category] = [];
    
    updated[category][index] = { 
      ...updated[category][index], 
      [field]: value 
    };
    setMenuItems(updated);
  };

  const handleToggleAvailable = (category, index) => {
    const updated = { ...menuItems };
    if (!updated[category]) updated[category] = [];
    
    updated[category][index] = { 
      ...updated[category][index], 
      available: !updated[category][index].available 
    };
    setMenuItems(updated);
  };

  const handleAddItem = (category) => {
    const newItem = {
      id: `item-${Date.now()}`,
      name: 'New Menu Item',
      description: 'Description of the new menu item',
      price: '£0.00',
      available: true
    };
    
    const updated = { ...menuItems };
    if (!updated[category]) updated[category] = [];
    updated[category].push(newItem);
    setMenuItems(updated);
    setEditingItem(`${category}-${updated[category].length - 1}`);
  };

  const handleDeleteItem = (category, index) => {
    if (window.confirm('Are you sure you want to delete this menu item?')) {
      const updated = { ...menuItems };
      updated[category].splice(index, 1);
      setMenuItems(updated);
      if (editingItem === `${category}-${index}`) {
        setEditingItem(null);
      }
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading menu...</div>;
  }

  return (
    <div className={styles['menu-editor']}>
      <div className={styles['editor-header']}>
        <h2>Menu Management</h2>
        <p>Manage restaurant menu sections, items and pricing.</p>
        <button
          onClick={() => setAddingSection(true)}
          className={styles['add-section-button']}
        >
          + Add Menu Section
        </button>
      </div>

      {/* Section Ordering Interface */}
      <div className={styles['section-ordering']}>
        <h3>Section Order</h3>
        <p>Change the order that sections appear on the website:</p>
        <div className={styles['section-order-list']} data-testid="section-order-list">
          {sectionOrder.map((sectionKey, index) => (
            <div 
              key={sectionKey} 
              className={styles['order-item']} 
              data-testid={`order-item-${sectionKey}`}
            >
              <span className={styles['order-number']}>{index + 1}.</span>
              <span className={styles['section-name']}>{getSectionLabel(sectionKey)}</span>
              <div className={styles['order-controls']}>
                <button
                  onClick={() => handleMoveSection(sectionKey, 'up')}
                  disabled={index === 0}
                  className={styles['move-button']}
                  aria-label="Move Up"
                  title="Move section up"
                >
                  ↑
                </button>
                <button
                  onClick={() => handleMoveSection(sectionKey, 'down')}
                  disabled={index === sectionOrder.length - 1}
                  className={styles['move-button']}
                  aria-label="Move Down"
                  title="Move section down"
                >
                  ↓
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      {addingSection && (
        <div className={styles['add-section-form']}>
          <div className={styles['form-row']}>
            <label>Section Name:</label>
            <input
              type="text"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              placeholder="Section name (e.g., Appetizers)"
              className={styles['form-input']}
              autoFocus
            />
          </div>
          <div className={styles['form-actions']}>
            <button
              onClick={handleAddSection}
              className={styles['confirm-button']}
              disabled={!newSectionName.trim()}
            >
              Add Section
            </button>
            <button
              onClick={() => {
                setAddingSection(false);
                setNewSectionName('');
              }}
              className={styles['cancel-button']}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className={styles['menu-categories']}>
        {sectionOrder.map(sectionKey => (
          <div 
            key={sectionKey} 
            className={styles['category-section']}
            data-testid={`section-${sectionKey}`}
          >
            <div className={styles['category-header']}>
              <h3>{getSectionLabel(sectionKey)}</h3>
              <div className={styles['section-actions']}>
                <button
                  onClick={() => handleAddItem(sectionKey)}
                  className={styles['add-item-button']}
                >
                  + Add Item
                </button>
                <button
                  onClick={() => handleDeleteSection(sectionKey)}
                  className={styles['delete-section-button']}
                  title="Delete entire section"
                >
                  🗑
                </button>
              </div>
            </div>

            <div className={styles['items-list']}>
              {(menuItems[sectionKey] || []).map((item, index) => (
                <div 
                  key={item.id || index} 
                  className={`${styles['item-card']} ${!item.available ? styles.unavailable : ''}`}
                >
                  <div className={styles['item-header']}>
                    <div className={styles['item-status']}>
                      <button
                        onClick={() => handleToggleAvailable(sectionKey, index)}
                        className={`${styles['status-toggle']} ${item.available ? styles.active : styles.inactive}`}
                      >
                        {item.available ? '✓ Available' : '✗ Unavailable'}
                      </button>
                    </div>
                    <div className={styles['item-actions']}>
                      <button
                        onClick={() => setEditingItem(editingItem === `${sectionKey}-${index}` ? null : `${sectionKey}-${index}`)}
                        className={styles['edit-button']}
                      >
                        {editingItem === `${sectionKey}-${index}` ? 'Done' : 'Edit'}
                      </button>
                      <button
                        onClick={() => handleDeleteItem(sectionKey, index)}
                        className={styles['delete-button']}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  {editingItem === `${sectionKey}-${index}` ? (
                    <div className={styles['item-form']}>
                      <div className={styles['form-row']}>
                        <label>Name:</label>
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleItemUpdate(sectionKey, index, 'name', e.target.value)}
                          className={styles['form-input']}
                        />
                      </div>
                      <div className={styles['form-row']}>
                        <label>Price:</label>
                        <input
                          type="text"
                          value={item.price || ''}
                          onChange={(e) => handleItemUpdate(sectionKey, index, 'price', e.target.value)}
                          className={styles['form-input']}
                          placeholder="£0.00"
                        />
                      </div>
                      <div className={styles['form-row']}>
                        <label>Description:</label>
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => handleItemUpdate(sectionKey, index, 'description', e.target.value)}
                          className={styles['form-textarea']}
                          rows="3"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className={styles['item-display']}>
                      <div className={styles['item-title-row']}>
                        <h4>{item.name}</h4>
                        <span className={styles['item-price']}>{item.price}</span>
                      </div>
                      <p className={styles['item-description']}>{item.description}</p>
                    </div>
                  )}
                </div>
              ))}

              {(!menuItems[sectionKey] || menuItems[sectionKey].length === 0) && (
                <div className={styles['empty-category']}>
                  <p>No items in this section yet.</p>
                  <button
                    onClick={() => handleAddItem(sectionKey)}
                    className={styles['add-first-item']}
                  >
                    Add First Item
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className={styles['editor-actions']}>
        <button
          onClick={() => window.location.reload()}
          className={styles['refresh-button']}
          disabled={saving}
        >
          Refresh Data
        </button>
        <button
          onClick={handleReset}
          className={styles['reset-button']}
          disabled={saving}
        >
          Reset Changes
        </button>
        <button
          onClick={handleSave}
          className={styles['save-button']}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className={styles['help-text']}>
        <h4>Tips:</h4>
        <ul>
          <li>Use the "Section Order" area at the top to reorder sections on the website</li>
          <li>Add custom sections with the "+ Add Menu Section" button</li>
          <li>Use "Available" to show items on the website, "Unavailable" to hide them</li>
          <li>Price format: £12.95 (include the £ symbol)</li>
          <li>Keep descriptions clear and mention any dietary requirements (v), (gf), etc.</li>
          <li>Changes are saved locally until you deploy updates to the live site</li>
        </ul>
      </div>
    </div>
  );
};

export default MenuEditor;
